var _a;
import { createSlice } from "@reduxjs/toolkit";
import { MenuItem, UnitSystem, ChecklistItemName, } from "./types";
import { CameraViewId } from "3d/types";
import { DEFAULT_LAYER } from "store/layers";
import { USE_LOCAL_GRAPH_DATA, hasAutoOpenedChartStorageKey, hasToggledChartStorageKey, } from "ui/components/panels/graphs/constants";
export var initialState = {
    cameraViewId: CameraViewId.EDIT_VIEW,
    unitSystem: UnitSystem.METRIC,
    activeMenuItem: null,
    is3DLoading: false,
    isGraphPanelOpen: USE_LOCAL_GRAPH_DATA ? true : false,
    isGraphPanelAutoOpened: false,
    checklist: (_a = {},
        _a[ChecklistItemName.ADD_GRIP_POSITION] = {
            translationKey: "checklist.add_grip_position",
            completed: false,
        },
        _a[ChecklistItemName.SELECT_CUP] = {
            translationKey: "checklist.select_cup",
            completed: false,
        },
        _a[ChecklistItemName.ADD_PUMP] = {
            translationKey: "checklist.add_pump",
            completed: false,
        },
        _a[ChecklistItemName.ADD_TUBE_CONNECTION] = {
            translationKey: "checklist.add_tube_connection",
            completed: false,
        },
        _a[ChecklistItemName.CONNECT_PARTS] = {
            translationKey: "checklist.connect_parts",
            completed: false,
        },
        _a[ChecklistItemName.CALCULATE] = {
            translationKey: "checklist.calculate",
            completed: false,
        },
        _a),
    selection: null,
    draggedAttachmentId: null,
    hoveredAttachmentId: null,
    highlightedNodeIds: [],
    hasPendingSanityCheck: true,
    activeLayer: DEFAULT_LAYER,
    cupFill: null,
    showPsEsIndicators: true,
};
var setDraggedAttachmentId = function (state, action) {
    state.draggedAttachmentId = action.payload;
};
var setHoveredAttachmentId = function (state, action) {
    state.hoveredAttachmentId = action.payload;
};
var setHighlightedNodeIds = function (state, action) {
    /* Keeps the array when the ids are the same, so clearing an empty highlight
     * does not repaint every node in the scene */
    var ids = action.payload;
    var current = state.highlightedNodeIds;
    if (ids.length === current.length && ids.every(function (id, i) { return id === current[i]; }))
        return;
    state.highlightedNodeIds = ids;
};
var setIs3DLoading = function (state, action) {
    state.is3DLoading = action.payload;
};
/*
 * The fill tool belongs to the selected cup — its button sits in that cup's
 * toolbar — so selecting anything else disarms it: nothing is drawn for the
 * mode except while it is armed, so it must never outlive its own control.
 */
var dropCupFillUnlessSelected = function (state) {
    var _a;
    if (!state.cupFill)
        return;
    var selectedIds = ((_a = state.selection) === null || _a === void 0 ? void 0 : _a.kind) === "entities" ? state.selection.ids : [];
    if (!selectedIds.includes(state.cupFill.anchorNodeId))
        state.cupFill = null;
};
var selectEntity = function (state, action) {
    var _a;
    if (!action.payload) {
        // Nothing to select: only a box or frame selection is dropped
        // (historical behaviour — an existing entity selection is left alone).
        if (((_a = state.selection) === null || _a === void 0 ? void 0 : _a.kind) !== "entities")
            state.selection = null;
        return;
    }
    state.selection = { kind: "entities", ids: [action.payload] };
    state.cameraViewId = CameraViewId.EDIT_VIEW;
    state.overlayData = undefined;
    state.activeMenuItem = MenuItem.SELECTED_ENTITY;
    dropCupFillUnlessSelected(state);
};
// Select a cardboard box. Boxes are their own selection kind (not "entities")
// so they don't open the node edit drawer; the single selection slot means any
// node selection is replaced.
var selectBox = function (state, action) {
    state.selection = action.payload ? { kind: "box", id: action.payload } : null;
    state.activeMenuItem = null;
    state.cameraViewId = CameraViewId.EDIT_VIEW;
    dropCupFillUnlessSelected(state);
};
/*
 * Select the cup tool's frame, which brings up its turn control. Like a box it
 * is its own selection kind, so it opens no node drawer and replaces whatever
 * was selected before.
 */
var selectGripFrameForEditing = function (state) {
    state.selection = { kind: "gripFrame" };
    state.activeMenuItem = null;
    state.cameraViewId = CameraViewId.EDIT_VIEW;
    dropCupFillUnlessSelected(state);
};
var setCameraView = function (state, action) {
    state.cameraViewId = action.payload;
    /* The fill tool previews on the flat 2D view; going to 3D ends it. */
    if (action.payload === CameraViewId.INITIAL_3D_VIEW)
        state.cupFill = null;
};
/* Arm the fill tool on a cup (see `dropCupFillUnlessSelected`). */
var startCupFill = function (state, action) {
    state.cupFill = action.payload;
};
var setCupFillGap = function (state, action) {
    if (!state.cupFill)
        return;
    state.cupFill.gap = action.payload;
};
var stopCupFill = function (state) {
    state.cupFill = null;
};
// Switch which stacking layer is shown. Any selected box belongs to the layer
// being left, so clear the box selection (its edit overlay would otherwise
// hang over a now-hidden box).
var setActiveLayer = function (state, action) {
    var _a;
    state.activeLayer = action.payload;
    if (((_a = state.selection) === null || _a === void 0 ? void 0 : _a.kind) === "box")
        state.selection = null;
};
var setUnitSystem = function (state, action) {
    state.unitSystem = action.payload;
};
var setOverlayData = function (state, action) {
    state.overlayData = action.payload;
};
var setShowPsEsIndicators = function (state, action) {
    state.showPsEsIndicators = action.payload;
};
var setActiveSidebarItemID = function (state, action) {
    state.activeSidebarItemID = action.payload;
};
var openMenuItem = function (state, action) {
    var _a;
    var goingBackFromMultiSelect = state.activeMenuItem === MenuItem.SELECTED_ENTITY &&
        action.payload === null &&
        ((_a = state.selection) === null || _a === void 0 ? void 0 : _a.kind) === "entities" &&
        state.selection.ids.length > 1;
    // if going back from the multi select menu after applying a filter, don't change menu item, just clear the selection filter
    if (goingBackFromMultiSelect && state.selectionFilter !== undefined) {
        state.selectionFilter = undefined;
        return;
    }
    state.activeMenuItem = action.payload;
    /* Deselect entities if user presses "back" button in menu*/
    if (!action.payload) {
        state.selection = null;
        state.cameraViewId = CameraViewId.EDIT_VIEW;
    }
};
/** The user opens or closes the graph panel: from here on it stays the way
 * they left it, and nothing nudges them towards it any more. */
var toggleGraphPanel = function (state, action) {
    sessionStorage.setItem(hasToggledChartStorageKey, "true");
    state.isGraphPanelOpen = action.payload;
    state.isGraphPanelAutoOpened = false;
};
/** The first calculation shows the charts to the user without being asked;
 * once per session (`hasAutoOpenedChartStorageKey`). */
var autoOpenGraphPanel = function (state) {
    sessionStorage.setItem(hasAutoOpenedChartStorageKey, "true");
    state.isGraphPanelOpen = true;
    state.isGraphPanelAutoOpened = true;
};
/** The user started using the self-opened panel, so it stays. */
var keepGraphPanelOpen = function (state) {
    state.isGraphPanelAutoOpened = false;
};
/** The self-opened panel has shown itself long enough. Does nothing once the
 * user has taken over the panel. Not counted as the user closing it, so the
 * dashboard buttons keep pulsing. */
var hideAutoOpenedGraphPanel = function (state) {
    if (!state.isGraphPanelAutoOpened)
        return;
    state.isGraphPanelOpen = false;
    state.isGraphPanelAutoOpened = false;
};
var setChecklistItemCompleted = function (state, action) {
    var _a = action.payload, itemName = _a.itemName, completed = _a.completed;
    var itemToComplete = state.checklist[itemName];
    itemToComplete.completed = completed;
};
var setChecklistItemHidden = function (state, action) {
    var _a = action.payload, itemName = _a.itemName, hidden = _a.hidden;
    var itemToHide = state.checklist[itemName];
    itemToHide.hidden = hidden;
};
var applySelectionFilter = function (state, action) {
    state.selectionFilter = action.payload;
};
var setHasPendingSanityCheck = function (state, action) {
    state.hasPendingSanityCheck = action.payload;
};
var selectEntities = function (state, action) {
    var unique = new Set();
    action.payload.forEach(function (id) { return unique.add(id); });
    state.selection = { kind: "entities", ids: Array.from(unique) };
    state.selectionFilter = undefined;
    state.cameraViewId = CameraViewId.EDIT_VIEW;
    state.activeMenuItem = MenuItem.SELECTED_ENTITY;
    dropCupFillUnlessSelected(state);
};
/*
 * Clear only the cardboard-box selection. Unlike `deselectEntities` this leaves
 * the sidebar menu and camera view alone: closing the box's parameter panel
 * shouldn't also close whatever the user has open in the sidebar.
 */
var deselectBox = function (state) {
    var _a;
    if (((_a = state.selection) === null || _a === void 0 ? void 0 : _a.kind) === "box")
        state.selection = null;
};
var deselectEntities = function (state) {
    state.selection = null;
    state.selectionFilter = undefined;
    state.cameraViewId = CameraViewId.EDIT_VIEW;
    state.activeMenuItem = null;
    state.cupFill = null;
    /* Sidebar drawers belong to the menu of whatever was selected. Keeping the
     * id would re-open that drawer as soon as the next entity is selected —
     * e.g. the cup family drawer, auto-opened for an empty grip position,
     * re-appearing for the next grip position even when it has a cup. */
    state.activeSidebarItemID = undefined;
};
var uiSlice = createSlice({
    name: "ui",
    initialState: initialState,
    reducers: {
        selectEntity: selectEntity,
        selectBox: selectBox,
        deselectBox: deselectBox,
        selectGripFrameForEditing: selectGripFrameForEditing,
        setCameraView: setCameraView,
        setActiveLayer: setActiveLayer,
        startCupFill: startCupFill,
        setCupFillGap: setCupFillGap,
        stopCupFill: stopCupFill,
        setOverlayData: setOverlayData,
        setShowPsEsIndicators: setShowPsEsIndicators,
        openMenuItem: openMenuItem,
        toggleGraphPanel: toggleGraphPanel,
        autoOpenGraphPanel: autoOpenGraphPanel,
        keepGraphPanelOpen: keepGraphPanelOpen,
        hideAutoOpenedGraphPanel: hideAutoOpenedGraphPanel,
        setIs3DLoading: setIs3DLoading,
        setUnitSystem: setUnitSystem,
        setChecklistItemCompleted: setChecklistItemCompleted,
        setChecklistItemHidden: setChecklistItemHidden,
        applySelectionFilter: applySelectionFilter,
        selectEntities: selectEntities,
        deselectEntities: deselectEntities,
        setActiveSidebarItemID: setActiveSidebarItemID,
        setDraggedAttachmentId: setDraggedAttachmentId,
        setHoveredAttachmentId: setHoveredAttachmentId,
        setHighlightedNodeIds: setHighlightedNodeIds,
        setHasPendingSanityCheck: setHasPendingSanityCheck,
    },
});
export var allActions = uiSlice.actions, reducer = uiSlice.reducer;
export var callableActions = {
    applySelectionFilter: allActions.applySelectionFilter,
    selectEntity: allActions.selectEntity,
    selectBox: allActions.selectBox,
    deselectBox: allActions.deselectBox,
    selectGripFrameForEditing: allActions.selectGripFrameForEditing,
    setCameraView: allActions.setCameraView,
    setActiveLayer: allActions.setActiveLayer,
    startCupFill: allActions.startCupFill,
    setCupFillGap: allActions.setCupFillGap,
    stopCupFill: allActions.stopCupFill,
    setOverlayData: allActions.setOverlayData,
    setShowPsEsIndicators: allActions.setShowPsEsIndicators,
    openMenuItem: allActions.openMenuItem,
    autoOpenGraphPanel: allActions.autoOpenGraphPanel,
    keepGraphPanelOpen: allActions.keepGraphPanelOpen,
    hideAutoOpenedGraphPanel: allActions.hideAutoOpenedGraphPanel,
    setIs3DLoading: allActions.setIs3DLoading,
    setUnitSystem: allActions.setUnitSystem,
    setChecklistItemCompleted: allActions.setChecklistItemCompleted,
    setChecklistItemHidden: allActions.setChecklistItemHidden,
    selectEntities: allActions.selectEntities,
    deselectEntities: allActions.deselectEntities,
    setActiveSidebarItemID: allActions.setActiveSidebarItemID,
    setDraggedAttachmentId: allActions.setDraggedAttachmentId,
    setHoveredAttachmentId: allActions.setHoveredAttachmentId,
    setHighlightedNodeIds: allActions.setHighlightedNodeIds,
    setHasPendingSanityCheck: allActions.setHasPendingSanityCheck,
};
export var internalActions = {
    toggleGraphPanel: allActions.toggleGraphPanel,
};
export default reducer;
